// src/pages/Login.jsx
import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

import BlissImageOne from "../images/BlissImageOne.jpg";
import BlissImageTwo from "../images/BlissImageTwo.jpg";
import BlissImageThree from "../images/BlissImageThree.jpg";
import logo1 from "../images/logo1.jpg";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const { login } = useAuth();
  const navigate = useNavigate();

  // Generate or get device ID
  const getDeviceId = () => {
    let deviceId = localStorage.getItem("device_id");
    if (!deviceId) {
      // Use your hardcoded device_id or generate a unique one
      deviceId = "0fa248833d29";
      localStorage.setItem("device_id", deviceId);
    }
    return deviceId;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
   if (isLoading) return;
    setIsLoading(true);

    console.log("🔐 Attempting login...", { username });

    try {
      const device_id = getDeviceId();
      console.log("📱 Using device_id:", device_id);

      await login({ username, password, device_id });
      
      console.log("✅ Login successful, redirecting to dashboard...");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("❌ Login failed:", err);
      const errorMessage = 
        err.response?.data?.detail || 
        err.response?.data?.message || 
        err.message ||
        "Login failed. Please check your credentials.";
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between bg-gradient-to-r from-blue-800 to-blue-600 text-white px-6 py-3 shadow-md">
        {/* Left: Logo */}
        <div className="flex items-center gap-2">
          <img src={logo1} alt="Logo" className="h-12 w-auto rounded-md" />
        </div>

        {/* Center: Reporting Purpose text */}
        <div className="flex flex-col items-center text-center">
          <p className="text-sm font-medium">
            This Site Is For Reporting Purpose
          </p>
        </div>

        {/* Right: Link */}
        <a
          href="#"
          className="text-yellow-300 text-sm font-semibold hover:underline"
        >
          New Mobile Application.
        </a>
      </header>

      {/* Main */}
      <main className="flex flex-1 items-center justify-center px-8 md:px-16 gap-10">
        {/* Left Section: Company Info */}
        <div className="hidden lg:flex flex-col justify-center gap-5 w-1/2">
          {[ 
            {
              img: BlissImageOne,
              title: "Leeford Healthcare Ltd",
              text: "Leeford is known for marketing quality healthcare products to create a healthier tomorrow...",
            },
            {
              img: BlissImageTwo,
              title: "Mission And Vision",
              text: "We dedicate ourselves to humanity's quest for longer, healthier, happier lives...",
            },
            {
              img: BlissImageThree,
              title: "Important Notes",
              text: "Please read the latest updates carefully.",
              color: "text-red-600 font-semibold",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 + idx * 0.1 }}
              className="flex flex-col items-start bg-white p-5 rounded-xl shadow-lg"
            >
              <img
                src={item.img}
                alt={item.title}
                className="w-16 h-16 rounded-full border-2 border-blue-700 mb-3 object-contain p-1"
              />
              <h3 className="text-blue-800 font-bold text-lg mb-2">
                {item.title}
              </h3>
              <p className={`text-sm leading-relaxed ${item.color || "text-gray-600"}`}>
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Right Section: Login Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md border-t-4 border-blue-700"
        >
          <h2 className="text-2xl font-bold text-center text-blue-800 mb-6">
            Sign In
          </h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Username */}
            <div>
              <label
                htmlFor="username"
                className="block text-gray-700 font-medium mb-1"
              >
                User Name
              </label>
              <input
                type="text"
                id="username"
                placeholder="Enter Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 outline-none text-sm"
                required
                disabled={isLoading}
              />
            </div>

            {/* Password */}
            <div className="relative">
              <label
                htmlFor="password"
                className="block text-gray-700 font-medium mb-1"
              >
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="Enter Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 outline-none text-sm"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full h-11 rounded-lg font-semibold text-white transition-all
                ${isLoading 
                  ? "bg-green-400 cursor-not-allowed" 
                  : "bg-green-600 hover:bg-green-700 hover:scale-105 active:scale-95"
                }`}
            >
              {isLoading ? "Logging in..." : "Log In"}
            </button>
          </form>

         
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="text-center py-4 bg-blue-800 text-white shadow-inner">
        <p>&copy; 2026, Leeford Healthcare Ltd. All Rights Reserved</p>
        <a href="https://www.leeford.in/" className="text-yellow-200 hover:underline text-xs block mt-1">
          www.leeford.in
        </a>
      </footer>
    </div>
  );
};

export default Login;